<template>
  <div
    class="f-col"
    :class="compact
      ? 'w-64 b b-white rounded-md bg-grey-80 shadow-lg'
      : 'w-full md:w-64 md:b md:b-white md:rounded-md md:bg-grey-80 md:shadow-lg'"
  >
    <component
      :is="productUrl ? BaseLink : BaseButton"
      :aria-haspopup="productUrl ? undefined : 'dialog'"
      class="text-left"
      :class="compact ? 'f-col p-0' : 'flex p-4 pt-0 md:f-col md:p-0'"
      :to="productUrl"
      @click="onCardClick"
    >
      <div
        v-style:aspect-ratio="{
          sm: mediaWidth.sm / mediaHeight.sm,
          md: mediaWidth.md / mediaHeight.md,
          lg: mediaWidth.lg / mediaHeight.lg,
        }"
        class="shrink-0"
        :class="compact ? 'w-full' : 'w-1/2 md:w-full'"
      >
        <base-picture
          v-if="product?.gallery"
          v-bind="product.gallery[0]"
          class="w-full overflow-hidden rounded-md"
          :height="mediaHeight"
          :src="imageSrc"
          :width="mediaWidth"
        />
        <div v-else class="full rounded-md skeleton" />
      </div>
      <div
        class="w-full overflow-hidden b-white p-4"
        :class="[
          compact ? 'rounded-b-md bg-grey-80' : 'md:rounded-b-md md:bg-grey-80',
          {
            'absolute bottom-0 left-0 right-0 bg-op-70 b b-t-0 backdrop-blur-sm': compact && layout === 'overlay',
            'md:absolute md:bottom-0 md:left-0 md:right-0 md:bg-op-70 md:b md:b-t-0 md:backdrop-blur-sm': !compact && layout === 'overlay',
            'b-t': compact && layout === 'below',
            'md:b-t': !compact && layout === 'below',
          },
        ]"
      >
        <template v-if="product">
          <p class="fw-bold">
            {{ product?.name }}
          </p>
          <product-pricing class-wrapper="text-xs " :currency="product.currency" :price="product.price" />
        </template>
        <template v-else>
          <div class="mb-4 h-6 w-full skeleton" />
          <div class="h-4 w-1/3 skeleton" />
        </template>
      </div>
    </component>
  </div>
</template>

<script lang="ts" setup>
import { BaseButton, BaseLink } from '#components'

const { productId, behavior, compact = false, layout = 'overlay', url } = defineProps<{
  url: string
  behavior: 'productcard' | 'hotzonequickshop' | 'oneclickhotzonequickshop'
  productId: string
  compact?: boolean
  layout?: 'overlay' | 'below'
}>()

const emit = defineEmits<{
  (e: 'openQuickshop', productId: string): void
}>()

const { mediaSize, mediaTransformations } = useAppConfig().components.product.card

const mediaWidth = normalizeResponsive(mediaSize.width)
const mediaHeight = normalizeResponsive(mediaSize.height)

const { product } = await useProduct(productId, {
  server: false,
  key: `hotzone-product-${productId}`,
  onResponseError: ({ response }) => {
    log.error(`[hot-zones] failed to load product ${productId}`, response?._data)
  },
})

const imageSrc = computed(() => {
  const image = product.value?.gallery?.[0]
  return getResponsiveImageTransformations(image?.src, {
    height: mediaHeight,
    width: mediaWidth,
    assetTransformation: image?.type === 'image' ? image.assetTransformation : undefined,
    ...mediaTransformations,
  })
})

const productUrl = computed(() => behavior === 'productcard' ? url : undefined)

const onCardClick = () => {
  if (behavior === 'hotzonequickshop' || behavior === 'oneclickhotzonequickshop') {
    emit('openQuickshop', productId)
  }
}
</script>
